// 브라우저와 서버 사이를 오가는 값의 모양 — 한 벌
//
// 이 디렉터리는 **발사대 스텝이 소유하고 이후 스텝은 읽기만 한다.** 갈래가 도는 동안
// 계약을 고치면 두 갈래가 서로 다른 계약 위에서 동시에 굳기 때문이다. 계약 개정이
// 필요해지면 갈래를 멈추고 개정 커밋을 먼저 낸다 — 계약 스냅샷 diff 가 커밋 전에 그
// 사실을 드러낸다.
//
// 무엇을 고정할지는 두 정본에서 뽑았다. 정본 요구사항 정의서의 1차 릴리스 범위 21개
// 항목과 화면 원칙 절, 그리고 판정 계약 37개 케이스가 관측 줄에서 요구하는 것이다.
// 같은 것을 요구하는 케이스들을 묶어 이름을 붙였고, 묶음은 다섯이다 — 접속·명부·
// 시퀀스·수비·시계 동기.

import { Type, type Static } from '@sinclair/typebox'

// ── 값 ────────────────────────────────────────────────────────────────────

/**
 * 모든 시간 값은 **정수 밀리초**로 오간다. 초 단위 실수를 전송 형식에 두지 않는 이유는
 * 이진 부동소수 오차가 경계를 넘나들며 쌓이기 때문이다. 브라우저와 서버 양쪽에서
 * src/core/time/brand.ts 의 브랜드 타입으로 감싸 쓴다.
 */
export const Millis = Type.Integer({ description: '정수 밀리초' })

/** UTC epoch 기준 절대 시각. 정수 밀리초. */
export const InstantMs = Type.Integer({ description: 'UTC epoch 기준 절대 시각(ms)' })

export const Id = Type.String({ minLength: 1, maxLength: 64 })

/**
 * 코드 알파벳은 Crockford Base32 다 — I·L·O·U 를 뺀다. 손으로 옮겨 적거나 음성으로
 * 부를 때 1과 I, 0과 O 를 헷갈리지 않기 위해서다. 공용 입장 코드는 10자(50비트),
 * room 관리 코드는 16자(80비트).
 */
export const CROCKFORD_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'
export const JOIN_CODE_LENGTH = 10
export const ADMIN_CODE_LENGTH = 16

export const JoinCode = Type.String({
  pattern: `^[${CROCKFORD_ALPHABET}]{${String(JOIN_CODE_LENGTH)}}$`,
  description: '공용 입장 코드 — Crockford Base32 10자',
})
export const AdminCode = Type.String({
  pattern: `^[${CROCKFORD_ALPHABET}]{${String(ADMIN_CODE_LENGTH)}}$`,
  description: 'room 관리 코드 — Crockford Base32 16자',
})

/**
 * 게임 ID 는 **자릿수 가변 숫자**다 (정본 계정 절의 예: "131952426"). 여기서 형식을
 * 못박는 이유는 이 값이 whitelist 대조의 열쇠이기 때문이다 — 대조는 문자열 같음으로
 * 하는데, 한쪽이 공백을 달고 들어오면 눈으로는 같은 ID 가 기계에서는 다른 값이 되고,
 * 그 사람은 "명단에 없다" 는 답만 받는다. 그래서 **명부 열쇠와 입장 값은 같은 규칙으로
 * 정규화돼야 한다.**
 *
 * - 앞뒤 공백 제거는 여러 번 해도 결과가 같은 정규화라 어느 쪽에서 해도 두 벌이 생기지 않는다.
 *   CT 화면은 명부에 보내기 전에, 서버는 입장 경로에서(`src/server/http.ts` 의 `trimGameId`,
 *   2026-10-01) 앞뒤 공백을 지운다. 다듬지 않던 때는 명단에 있는 ID 를 공백과 함께 붙여 넣은
 *   사람이 「숫자만」으로 거절됐다(2026-09-17 대리 관측).
 * - 서로 다른 숫자를 하나로 합치는 정규화(가운데 공백 제거, 앞자리 0 제거)는 어느 쪽에서도 하지
 *   않는다. 그것이 같은 사람을 두 벌로, 또는 남의 ID 로 만드는 길이다.
 * - 서버의 명부 등록은 다듬지 않고 아래 형식 검사로 거절한다.
 */
export const GAME_ID_MAX_DIGITS = 24

export const GameId = Type.String({
  pattern: `^[0-9]{1,${String(GAME_ID_MAX_DIGITS)}}$`,
  description: '인게임 고유 숫자 ID — 자릿수 가변',
})

export const Nickname = Type.String({ minLength: 1, maxLength: 64 })

export const OperationType = Type.Union([Type.Literal('attack'), Type.Literal('defense')])
export const RallyType = Type.Union([
  Type.Literal('main'),
  Type.Literal('strike'),
  Type.Literal('garrison'),
])
export const RosterSide = Type.Union([Type.Literal('ally'), Type.Literal('enemy')])
export const SequenceState = Type.Union([
  Type.Literal('draft'),
  Type.Literal('confirmed'),
  Type.Literal('dispatched'),
])

// ── 명부 ──────────────────────────────────────────────────────────────────

export const Roster = Type.Object({
  id: Id,
  side: RosterSide,
  nickname: Type.String({ minLength: 1, maxLength: 64 }),
  // 아군은 CT 가 입력한 실측 행군 시간, 적군은 누적 수집값이다. 아직 재지 못했으면 null.
  marchingMs: Type.Union([Millis, Type.Null()]),
})

// ── 시퀀스 ────────────────────────────────────────────────────────────────

/**
 * offset 은 **착탄축 값이다.** 개시축이 아니다. main 의 offset 은 언제나 0 이고
 * 나머지는 main 착탄 대비 차이다. 이 축을 개시축으로 바꾸면 같은 −1.0초가 1초 먼저가
 * 아니라 59초 늦게 맞는다 — 정본의 반례가 그것이다.
 */
export const Rally = Type.Object({
  id: Id,
  type: RallyType,
  rosterId: Id,
  rallyingMs: Millis,
  /**
   * 이 rally 를 맡은 사람의 행군 시간. **어디서 오는 값인지가 상태에 따라 다르다.**
   *
   * dispatch 전에는 명부의 **지금 값**을 그때그때 읽어 싣는다. 조립 중에 그 사람의
   * 실측이 갱신되면 미리보기가 따라 움직여야 하기 때문이다. dispatch 하는 순간 그 값이
   * 이 행에 **얼어붙고**, 그 뒤로는 명부가 어떻게 바뀌어도 이 값이 답이다.
   *
   * 규율이 아니라 **저장 구조로 가른 이유**가 있다. 「진행 중 시퀀스는 구성 시점 delta 로
   * 확정된다」가 판정 계약의 불통 조항인데, 규율로 두면 어느 코드 한 줄이 명부를 다시
   * 읽는 순간 조용히 깨지고 그 어긋남은 화면에 보이지 않는다 — 시각이 그럴듯하게 찍히기
   * 때문이다. 얼리는 자리를 하나로 두면 깨뜨리려면 그 한 줄을 지나야 한다.
   */
  marchingMs: Millis,
  offsetMs: Millis,
})

export const Sequence = Type.Object({
  state: SequenceState,
  // dispatch 되기 전에는 앵커가 없다. 구성 화면 본문이 delta 전용인 이유가 이것이다 —
  // 절대 시각이 아직 존재하지 않는다.
  impactMainAt: Type.Union([InstantMs, Type.Null()]),
  /**
   * 시간 손잡이 — **가장 이른 개시를 지금부터 얼마 뒤에 놓을 것인가.**
   *
   * `min(open_i) = now + handle` 이고 나머지가 전부 여기서 산출된다. 시퀀스가 강체라
   * 어느 시각 하나를 정하면 전부 따라오므로 `open_main` 입력 칸을 따로 두지 않는다.
   *
   * **조립 중에는 `null` 이다.** 손잡이는 `sequence confirm` 뒤의 편집 문맥에 속하고 그
   * 전에는 화면의 DOM 에도 존재하지 않아야 한다. 값이 없다는 것을 데이터가 함께 말해
   * 두면 그 규칙이 화면 쪽 규율 하나에만 걸려 있지 않게 된다.
   */
  handleMs: Type.Union([Millis, Type.Null()]),
  rallies: Type.Array(Rally),
})

// ── 수비 ──────────────────────────────────────────────────────────────────

/**
 * 적 rally 를 관측한 순간 그것이 어느 단계에 있었는가. **역산이 이 값으로 갈린다.**
 *
 * 2026-08-10 인게임 실측이 착탄 역산을 하나에서 둘로 갈랐다. 같은 `0:52` 가 rallying
 * 중이면 「아직 출발하지 않았다」이고 행군 중이면 「이미 날아오고 있다」라, 단계를 안 받으면
 * 착탄이 **적 marching 하나만큼 통째로 어긋난다.**
 */
export const EnemyPhase = Type.Union([Type.Literal('rallying'), Type.Literal('marching')])

export const EnemyRally = Type.Object({
  id: Id,
  enemyRosterId: Id,
  /**
   * 입력 앵커 — CT 가 인게임 카운트다운을 **본 순간의 서버 시각**이다. 착탄은 이 셋에서
   * 역산하고, 행 수정은 값 갱신이 아니라 **재앵커**다 — 사람이 값을 다시 본 순간이 새
   * 기준이기 때문이다.
   */
  anchoredAt: InstantMs,
  phase: EnemyPhase,
  /**
   * 그 순간 화면이 보여 준 잔여 시간. **단계에 따라 뜻이 다르다** — rallying 이면 남은
   * rallying, 행군이면 남은 행군이다.
   *
   * 칸 이름에 `Rallying` 을 넣지 않는 이유가 그것이다. 이름이 한쪽 단계를 가리키면 다른
   * 단계의 값이 그 칸에 들어갈 때마다 읽는 사람이 한 번씩 틀린 뜻으로 읽는다.
   */
  remainingMs: Millis,
  /**
   * 역산된 착탄. **rallying 앵커인데 적 marching 을 아직 모으지 못했으면 `null` 이다** —
   * 「착탄 미상」이 그것이다.
   *
   * **행군 앵커에는 미상이 없다.** 그 식에 적 marching 항이 없어서 명부가 비어 있어도
   * 착탄이 확정된다. 「착탄 미상」을 우회하는 유일한 경로가 그것이라, 거기서 미상이 나면
   * 2026-08-10 개정이 아무것도 하지 않은 것이다.
   */
  impactAt: Type.Union([InstantMs, Type.Null()]),
})

// ── 접속 ──────────────────────────────────────────────────────────────────

/**
 * 접속 명단은 **게임 ID 당 한 줄이고 접속 수를 병기한다.** 같은 ID 의 다중 접속을
 * 막지 않기로 한 2026-08-06 절단의 결과다. 명단의 정확도는 heartbeat 하나에 걸린다.
 */
export const PresenceEntry = Type.Object({
  gameId: GameId,
  nickname: Nickname,
  connections: Type.Integer({ minimum: 0 }),
})

/**
 * whitelist 한 줄 — 게임 ID 와 닉네임 쌍이다. **이 명단이 이 제품의 방어선이다.**
 * 비밀번호가 없으므로 코드가 새면 남이 들어올 수 있는데, 그때 막는 것은 인증이 아니라
 * "명단에 없는 ID 는 코드를 알아도 못 들어온다" 이다.
 *
 * 그리고 이 값은 **다시 못 모은다** — 인게임에서 한 번씩 눈으로 재어 모은 것이라
 * 코드처럼 재발급할 수 없다. 어느 복구 경로도 이 명단을 건드리지 않는 이유가 그것이다.
 */
export const WhitelistEntry = Type.Object({
  gameId: GameId,
  nickname: Nickname,
  /**
   * 이 사람의 행군 시간. **아군 명부가 곧 이 명단이다.**
   *
   * 칸을 여기 붙인 이유: 정본은 명부의 데이터에 게임 ID 가 있다고 적고, 같은 문서가
   * whitelist 에 게임 ID 와 닉네임을 적는다. 둘은 같은 사람 목록의 두 얼굴이고,
   * **두 벌로 만들면 게임 ID 와 닉네임의 짝이 두 곳에 생겨 반드시 어긋난다.** 어긋난
   * 순간 입장 매칭과 rally 배정이 서로 다른 사람을 가리킨다.
   *
   * 아직 못 받았으면 null 이다. CT 는 사람을 먼저 명단에 올리고 그 사람이 인게임에서
   * 재어 보고할 때 값을 채운다 — 순서가 그렇게 흐르므로 NOT NULL 을 걸 수 없다.
   */
  marchingMs: Type.Union([Millis, Type.Null()]),
})

/** 명단에 올릴 때 보내는 것. marching 은 아직 없을 수 있어 생략할 수 있다. */
export const WhitelistDraft = Type.Object({
  gameId: GameId,
  nickname: Nickname,
  marchingMs: Type.Optional(Type.Union([Millis, Type.Null()])),
})

/**
 * 코드 패널과 `scripts/room-codes.sh` 가 내놓는 한 벌.
 *
 * **관리 코드가 평문으로 여기 실린다.** 저장소에는 해시로 두면서 이 응답에는 값을
 * 싣는 것이 모순처럼 보이지만 아니다 — 값의 금고는 데이터 볼륨의 `room-codes.txt`
 * (권한 600)이고, 저장소의 해시는 대조용 사본이다. 둘을 가른 이유는 저장소 파일만
 * 새는 경로(백업 파일을 남에게 넘기는 것 같은)에서 관리 코드가 함께 새지 않게
 * 하려는 것이다. 이 응답은 관리 코드로 자기를 증명한 CT 에게만 나간다.
 *
 * room 수를 함께 내는 이유는 「이 서버에 room 은 하나」가 지금도 참인지를 CT 가 눈으로
 * 확인할 수 있어야 하기 때문이다. 마지막 발급 시각은 재발급이 실제로 일어났는지를
 * 값을 외우지 않고도 구별하게 해 준다.
 */
export const RoomCodes = Type.Object({
  joinCode: JoinCode,
  adminCode: AdminCode,
  roomCount: Type.Integer({ minimum: 0 }),
  issuedAt: InstantMs,
})

/** 재발급 대상. 둘을 한 번에 갈지 않는다 — 아래 라우트 주석에 이유가 있다. */
export const RotateTarget = Type.Union([Type.Literal('admin'), Type.Literal('join')])

export const RoomPublic = Type.Object({
  operation: OperationType,
  // 입장 두 번째 단계가 보여 줄 CT 닉네임. 서버 기동 직후에는 아직 비어 있고, 그때는
  // 올바른 공용 입장 코드도 거절된다 — 보여 줄 닉네임이 없으면 오입장을 잡는다는 그
  // 단계의 존재 이유가 사라지기 때문이다.
  ctNickname: Type.Union([Type.String({ minLength: 1, maxLength: 64 }), Type.Null()]),
})

// ── 시계 동기 ─────────────────────────────────────────────────────────────

/**
 * 네 타임스탬프 왕복. t0 은 브라우저가 보낸 순간, t1 은 서버가 받은 순간, t2 는 서버가
 * 답한 순간, t3 은 브라우저가 받은 순간이다.
 *
 * 왕복이 크다고 시계 오차가 그만큼 커지지는 않는다. 오차를 만드는 것은 왕복의 크기가
 * 아니라 **가는 길과 오는 길의 비대칭**이라, 두 방향이 대칭이면 왕복 400밀리초에서도
 * 계산된 시각 차이는 정확하다. 그래서 먼 접속자에게 필요한 것은 가까운 서버가 아니라
 * 제대로 만든 시계 동기다.
 */
export const ClockSyncRequest = Type.Object({ t0: InstantMs })
export const ClockSyncResponse = Type.Object({
  t0: InstantMs,
  t1: InstantMs,
  t2: InstantMs,
  // 서버가 자기 이벤트 루프 지연을 함께 알린다. 이 값이 크면 t1·t2 사이가 늘어나
  // 편차 추정이 흔들리므로, 화면이 아니라 원장이 이 수치를 본다.
  eventLoopLagMs: Millis,
})

export type Roster = Static<typeof Roster>
export type Rally = Static<typeof Rally>
export type Sequence = Static<typeof Sequence>
export type EnemyRally = Static<typeof EnemyRally>
export type PresenceEntry = Static<typeof PresenceEntry>
export type WhitelistEntry = Static<typeof WhitelistEntry>
export type RoomCodes = Static<typeof RoomCodes>
export type RotateTarget = Static<typeof RotateTarget>
export type RoomPublic = Static<typeof RoomPublic>
export type ClockSyncRequest = Static<typeof ClockSyncRequest>
export type ClockSyncResponse = Static<typeof ClockSyncResponse>
